/**
 * THE GRID STYLE CONTRACT — one source of truth for surface, row height,
 * density and cell padding. Measured against MUI core v9.4 / X v9.13.
 *
 *   ┌─ host owns the CARD ────────────────────────────────────────┐
 *   │  <AgCollapsibleCard>        ← chrome: border, radius, px-6  │
 *   │     <DataGrid surface="bare" />   ← chrome: NONE            │
 *   └─────────────────────────────────────────────────────────────┘
 *   A grid never decides whether it lives in a card. The host does.
 *
 *   surface="card"  grid paints its own white card   (24px pad)
 *   surface="bare"  grid paints nothing              (0px pad)
 *
 * WHY the seam: a grid that omitted `noBorder` inside a card drew a
 * SECOND card — 24px (card px-6) + 24px (grid p:3) = 48px of padding,
 * two borders, two radii. That is the staging padding bug.
 *
 *   token                       value   who applies it
 *   ─────────────────────────── ─────── ──────────────────────────
 *   GRID_CARD_PADDING           3 (24px) gridSurfaceSx('card')
 *   GRID_CARD_RADIUS            10px     gridSurfaceSx('card')
 *   MIN_ROW_HEIGHT              44px     gridRowHeight()
 *   GRID_COLUMN_HEADER_HEIGHT   40px     DataGrid's `columnHeaderHeight`
 *   GRID_CELL_PADDING_X         10px     MUI v9 default — DO NOT set
 *
 * ROW HEIGHT is `MIN_ROW_HEIGHT × densityFactor`, and nothing else.
 * Density factors are MUI's: compact .7 · standard 1 · comfortable 1.3.
 * So 44 × .7 = 30.8px compact, 44px standard.
 *
 *   ✗ getRowHeight={({densityFactor}) => MIN_ROW_HEIGHT * densityFactor}
 *   ✓ (omit it — `DataGrid` already defaults to exactly this)
 *   ✓ getRowHeight={gridRowHeight}   ← only if a prop is required
 *
 * That lambda is hand-rolled at 8 call sites today. Each copy is a
 * place the number can drift; delete them, do not add a ninth.
 *
 * MEASURED, not assumed — MUI X v9.13 defaults, for whoever converges
 * the outliers next:
 *   rowHeight 52 · columnHeaderHeight 56 · cell padding `0 10px`
 * We override the first two (44 / 40) and inherit the third. Header
 * height is told to MUI via the `columnHeaderHeight` PROP, never via
 * CSS: MUI sizes the top container from the prop, so a CSS-only
 * override leaves the difference as a grey band above row 1. The
 * "core's 48px default" comment repeated at 4 call sites is STALE —
 * 48 was never this version's default, and `EditableDataGrid` still
 * carries its own `ROW_HEIGHT_PX = 48`. Both are known divergences,
 * not the contract.
 *
 * CELL PADDING is MUI's `0 10px`. `baseGridStyles` deliberately sets
 * no padding on `.MuiDataGrid-cell` — inheriting is what keeps every
 * grid identical. `GRID_CELL_PADDING_X` exists so a surface that must
 * state the number states THIS one.
 *
 * Adding a style here changes EVERY grid in the app. Add per-surface
 * overrides at the call site's `sx`, never here.
 */
import type { SxProps, Theme } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-premium';

export const MIN_ROW_HEIGHT = 44;

/** Header row height. Overrides MUI v9's 56px default on every grid. */
export const GRID_COLUMN_HEADER_HEIGHT = 40;

/**
 * MUI v9's own horizontal cell padding. Mirrored, NOT applied — the
 * contract is that we inherit it. Read it; don't write a second one.
 */
export const GRID_CELL_PADDING_X = 10;

/** Card padding in theme spacing units — 3 × 8px = 24px. */
export const GRID_CARD_PADDING = 3;

export const GRID_CARD_RADIUS = '10px';

/**
 * Who paints the card.
 *
 *   'card'  the grid draws it  — grid is the only thing on the page
 *   'bare'  the host drew it   — grid sits inside a card/panel/modal
 */
export type GridSurface = 'card' | 'bare';

/**
 * `'bare'` is EMPTY, not "transparent" — the wrapper must contribute no box of its own.
 * Returns a PLAIN object, not `SxProps<Theme>`: the caller spreads it into a larger `sx`, and
 * `SxProps` is a union (object | fn | array) that cannot be spread.
 */
export type GridSurfaceSx = { bgcolor?: string; borderRadius?: string; p?: number };

export const gridSurfaceSx = (surface: GridSurface): GridSurfaceSx =>
  surface === 'card' ? { bgcolor: 'white', borderRadius: GRID_CARD_RADIUS, p: GRID_CARD_PADDING } : {};

/**
 * Resolve the surface seam. BEHAVIOUR-NEUTRAL BY CONSTRUCTION — with
 * no `surface` prop this reproduces the pre-seam `noBorder` ternary
 * exactly, so every existing call site renders as it did:
 *
 *   surface   noBorder   result
 *   ────────  ─────────  ──────
 *   'card'    any        'card'   ← explicit host opt-in wins
 *   'bare'    any        'bare'   ← explicit host opt-in wins
 *   undefined true       'bare'
 *   undefined false/—    'card'   ← today's default for 27 grids
 *
 * `surface` is the seam later slices move each surface over on;
 * `noBorder` stays the legacy spelling until they have all moved.
 */
export const resolveGridSurface = (options: { surface?: GridSurface; noBorder?: boolean }): GridSurface =>
  options.surface ?? (options.noBorder ? 'bare' : 'card');

/**
 * THE row-height rule: `MIN_ROW_HEIGHT × densityFactor`. `DataGrid`
 * already applies this when `getRowHeight` is omitted — import this
 * only where a `getRowHeight` prop is structurally required.
 */
export const gridRowHeight = ({ densityFactor }: { densityFactor: number }): number => MIN_ROW_HEIGHT * densityFactor;

// d for descendant, idk i just wanted it to be short
const d = (cls: string) => `& .${cls}`;

export type BaseGridStylesOptions = { variant?: 'list' | 'grid'; noBorder?: boolean };

export const baseGridStyles = (variantOrOptions: 'list' | 'grid' | BaseGridStylesOptions = 'grid'): SxProps<Theme> => {
  const options: BaseGridStylesOptions =
    typeof variantOrOptions === 'string' ? { variant: variantOrOptions } : variantOrOptions;
  const { variant = 'grid', noBorder = false } = options;

  return {
    border: 'none',
    outline: 'none',
    bgcolor: noBorder ? 'transparent' : 'white',
    overflow: 'visible',

    [d(gridClasses.main)]: {
      display: 'grid',
      borderRadius: '10px',
      outline: '1px solid var(--color-neutral-5)',
      bgcolor: 'white',
    },

    // The host card owns the outer edge; the virtual scroller keeps its own clipping.
    // Visible overflow turns flex `min-height: auto` into the full row height; 0 keeps fixed-height grids scrolling.
    [d(gridClasses.mainContent)]: { overflow: 'visible', minHeight: 0 },

    // Bottom padding stays with each toolbar: legacy Toolbar varies it by searchbar, v2 wants 0.
    // A `pb` here is 0,2,0 and silently flattens both.
    [d(gridClasses.toolbarContainer)]: { bgcolor: 'transparent', borderBottom: 0 },

    [d(gridClasses.columnHeaders)]: { bgcolor: 'grey.100' },

    [`& .${gridClasses.scrollbarFiller}--header`]: {
      bgcolor: variant === 'list' ? 'var(--color-neutral-2)' : 'grey.100',
    },

    [`& .${gridClasses.scrollbarFiller}--header.${gridClasses.scrollbarFiller}--pinnedRight`]: {
      bgcolor: `${variant === 'list' ? 'var(--color-neutral-2)' : 'grey.100'} !important`,
    },

    [`& .${gridClasses.scrollbarFiller}--borderTop.${gridClasses.scrollbarFiller}--pinnedRight`]: {
      bgcolor: 'white !important',
    },

    [d(gridClasses.columnHeader)]: {
      bgcolor: variant === 'list' ? 'var(--color-neutral-2)' : 'grey.100',
      borderColor: 'grey.300',
      '&:last-child': { borderRight: 'none' },
      '&:focus-within, &:focus': { outline: 'none' },
      '& p': { fontWeight: 700, letterSpacing: '0.17px' },
      [d(gridClasses.columnHeaderTitle)]: { letterSpacing: '0.17px', fontWeight: 700 },
      '&[data-field="__tree_data_group__"]': { borderRight: 0 },
    },

    [d(gridClasses.columnHeaderTitle)]: { fontWeight: 700 },

    [d(gridClasses['row--editing'])]: { boxShadow: 'none' },

    [d(gridClasses.row)]: {
      // styling for editable rows

      [`&.${gridClasses['row--editable']}`]: {
        bgcolor: 'white !important',
        borderRight: 0,
        '&:hover': {
          [d(gridClasses.cell)]: { bgcolor: 'primary.50', [`&.${gridClasses['cell--editing']}`]: { bgcolor: 'white' } },
        },
        [d(gridClasses.cell)]: {
          bgcolor: 'white',
          [`&:not(.${gridClasses['cell--editable']})`]: { bgcolor: 'grey.25' },
          [`&.Mui-disabled`]: { bgcolor: 'grey.25' },
        },
      },

      // styling for non editable rows
      [`&:not(.${gridClasses['row--editable']})`]: {
        [d(gridClasses.cell)]: { bgcolor: 'white' },
        '&:hover': {
          [d(gridClasses.cell)]: {
            '&[data-field="__tree_data_group__"]': { bgcolor: '#F2F5E6 !important' },
            bgcolor: 'primary.50',
          },
        },
      },
    },

    [d(gridClasses.cell)]: {
      display: 'flex',
      alignItems: 'center',
      borderColor: 'grey.300',
      '&:last-of-type': { borderRight: 'none' },
      '&:focus-within, &:focus': { outline: 'none' },
      '&[data-field="__tree_data_group__"]': { bgcolor: 'white !important', borderRight: 0 },
      '&[data-field="actions"]': { bgcolor: 'white' },
      '&.white': { bgcolor: 'white' },
      ...(variant === 'list' ? { borderRight: 0, borderLeft: 0 } : { borderRight: '1px solid var(--color-neutral-5)' }),
    },

    // The header row already draws the divider; the blanket `borderColor` above would repaint
    // the top border MUI cancels here, doubling it.
    [`& .${gridClasses['row--firstVisible']} .${gridClasses.cell}`]: { borderTopColor: 'transparent' },

    [d(gridClasses['container--bottom'])]: {
      '&:hover': {
        [d(gridClasses.cell)]: { bgcolor: 'primary.50', [`&.${gridClasses['cell--editing']}`]: { bgcolor: 'white' } },
      },
      [d(gridClasses.cell)]: { bgcolor: 'white !important' },
    },

    [d(gridClasses['row--dynamicHeight'])]: { minHeight: `${MIN_ROW_HEIGHT}px !important` },

    [d(gridClasses.aggregationColumnHeaderLabel)]: { display: 'none' },

    [d(gridClasses.footerContainer)]: { borderTop: 'none' },

    '@media print': { [d(gridClasses.footerContainer)]: { display: 'none' } },

    '& .AutoCompleteCell-edit': { padding: '0 !important' },
  };
};
